import React from 'react'
import { ErrorItem, ErrorsIndicator } from './ErrorsIndicator'
import { EffectiveStatus } from '@marketing-milk/interfaces'

type Props = {
  status?: EffectiveStatus | null
  hasEnded?: boolean
  isFuture?: boolean
  className?: string
  errors?: ErrorItem[]
  drawerTitle?: string
  drawerDescription?: string
}

type Tone = 'success' | 'info' | 'quiet' | 'danger'

const toneClasses: Record<Tone, string> = {
  success: 'bg-mm-success-100 text-mm-success-800',
  info: 'bg-mm-turquoise-100 text-mm-turquoise-800',
  quiet: 'bg-mm-neutral-2-100 text-mm-neutral-2-700',
  danger: 'bg-mm-warning-100 text-mm-warning-800',
}

interface EffectiveStatusDisplayProps {
  tone: Tone
  text: string
  endedText?: string
  futureText?: string
}

const effectiveStatusRecord: Record<EffectiveStatus, EffectiveStatusDisplayProps> = {
  ACTIVE: { tone: 'success', text: 'Active', endedText: 'Completed', futureText: 'Scheduled' },
  PAUSED: { tone: 'info', text: 'Paused', endedText: 'Off' },
  DELETED: { tone: 'quiet', text: 'Deleted' },
  ARCHIVED: { tone: 'quiet', text: 'Archived' },
  IN_PROCESS: { tone: 'info', text: 'Processing' },
  WITH_ISSUES: { tone: 'danger', text: 'Errors' },
  CAMPAIGN_PAUSED: { tone: 'info', text: 'Campaign Paused' },
  PENDING_REVIEW: { tone: 'info', text: 'In Review' },
  DISAPPROVED: { tone: 'danger', text: 'Rejected' },
  PREAPPROVED: { tone: 'info', text: 'Preapproved' },
  PENDING_BILLING_INFO: { tone: 'danger', text: 'Billing Errors' },
  ADSET_PAUSED: { tone: 'info', text: 'Adset Paused' },
}

export const StatusIndicator = ({ status, hasEnded, isFuture, ...props }: Props) => {
  // Statuses reach this from Facebook as well as from us, so an unmapped one shows its own
  // name rather than crashing on a missing entry and taking the page down with it.
  const display = status ? effectiveStatusRecord[status] : undefined
  const tone: Tone = display?.tone ?? 'danger'

  const displayStatus = (campaignHasEnded?: boolean, isFuture?: boolean): string => {
    if (!status) return 'Incomplete'
    if (!display) return status
    // statuses that may change based upon whether or not the campaign has finished
    const { endedText, futureText, text } = display
    return campaignHasEnded && endedText ? endedText : isFuture && futureText ? futureText : text
  }

  return (
    <div className={`flex justify-center ${props.className || ''}`}>
      {props.errors && props.errors.length ? (
        <ErrorsIndicator
          errors={props.errors}
          drawerDescription={props.drawerDescription}
          drawerTitle={props.drawerTitle}
        />
      ) : (
        <span
          className={`${toneClasses[tone]} inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium`}
        >
          {displayStatus(hasEnded, isFuture)}
        </span>
      )}
    </div>
  )
}
